/* Upgrade safely on both initial load and theme-editor section reloads. */
(() => {
  if (!window.customElements || customElements.get('lpl-science-fill')) return;

  class LplScienceFill extends HTMLElement {
    connectedCallback() {
      this._cleanup();
      this._generation = (this._generation || 0) + 1;
      const generation = this._generation;
      this._inView = false;
      this._imageReady = false;
      this._preparing = false;
      this._motion = window.matchMedia?.('(prefers-reduced-motion: reduce)');

      if (this._revealed || this.dataset.fillState === 'visible' || this.dataset.fillState === 'complete' ||
          this._motion?.matches || !window.IntersectionObserver) {
        this._reveal('complete');
        return;
      }

      this.dataset.fillState = 'waiting';
      this._motionChanged = (event) => {
        if (event.matches) this._reveal('complete');
      };
      this._motion?.addEventListener?.('change', this._motionChanged);
      this._observer = new IntersectionObserver((entries) => {
        if (generation !== this._generation || !this.isConnected) return;
        this._inView = entries.some((entry) => entry.isIntersecting && entry.intersectionRatio >= 0.2);
        if (!this._inView) return;
        if (this._imageReady) this._reveal();
        else this._prepareImage();
      }, { threshold: 0.2 });
      this._observer.observe(this);
    }

    disconnectedCallback() {
      this._generation = (this._generation || 0) + 1;
      this._cleanup();
    }

    _prepareImage() {
      if (this._preparing || this._revealed || !this.isConnected) return;
      this._preparing = true;
      const generation = this._generation;
      const ready = () => {
        if (generation !== this._generation || !this.isConnected || this._imageReady) return;
        this._imageReady = true;
        this._cleanupImage();
        if (this._inView) this._reveal();
      };

      this._image = this.querySelector('img');
      if (!this._image) {
        ready();
        return;
      }

      const image = this._image;
      this._imageLoaded = () => {
        if (generation !== this._generation || this._imageReady) return;
        if (typeof image.decode !== 'function' || !image.naturalWidth) {
          ready();
          return;
        }
        try {
          Promise.resolve(image.decode()).then(ready, ready);
        } catch (_) {
          ready();
        }
      };
      this._imageErrored = ready;
      image.addEventListener('load', this._imageLoaded, { once: true });
      image.addEventListener('error', this._imageErrored, { once: true });
      // A stalled image/decode must never leave an in-view figure hidden.
      this._imageTimeout = window.setTimeout(ready, 3000);
      if (image.complete) this._imageLoaded();
    }

    _reveal(state = 'visible') {
      if (!this.isConnected) return;
      this._revealed = true;
      this.dataset.fillState = state;
      this._generation = (this._generation || 0) + 1;
      this._cleanup();
    }

    _cleanupImage() {
      window.clearTimeout(this._imageTimeout);
      this._image?.removeEventListener('load', this._imageLoaded);
      this._image?.removeEventListener('error', this._imageErrored);
      this._image = null;
      this._imageTimeout = null;
    }

    _cleanup() {
      this._observer?.disconnect();
      this._observer = null;
      this._cleanupImage();
      this._motion?.removeEventListener?.('change', this._motionChanged);
      this._motion = null;
    }
  }

  customElements.define('lpl-science-fill', LplScienceFill);
})();
